import { Auth } from 'aws-amplify';

const BASE = process.env.REACT_APP_API_URL || '';

async function getToken() {
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch {
    return null;
  }
}

async function request(method, path, body) {
  const token = await getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const masqId = sessionStorage.getItem('masq_user_id');
  if (masqId) headers['X-Masquerade-As'] = masqId;

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const e = new Error(err.error || err.message || `HTTP ${res.status}`);
    e.status = res.status;
    throw e;
  }
  return res.json();
}

export const api = {
  get:    (path)        => request('GET',    path),
  post:   (path, body)  => request('POST',   path, body),
  put:    (path, body)  => request('PUT',    path, body),
  patch:  (path, body)  => request('PATCH',  path, body),
  delete: (path)        => request('DELETE', path),
};

// ── Posts ────────────────────────────────────────────────────────────────────
export const postsApi = {
  list:      (params = '')         => api.get(`/api/posts${params}`),
  create:    (data)                => api.post('/api/posts', data),
  react:     (id, emoji)           => api.post(`/api/posts/${id}/react`, { emoji }),
  comment:   (id, content)         => api.post(`/api/posts/${id}/comments`, { content }),
  reactors:  (id, { emoji, cursor, limit } = {}) => {
    const qs = new URLSearchParams();
    if (emoji)  qs.set('emoji',  emoji);
    if (cursor) qs.set('cursor', cursor);
    if (limit)  qs.set('limit',  String(limit));
    const q = qs.toString();
    return api.get(`/api/posts/${id}/reactions${q ? `?${q}` : ''}`);
  },
};

// ── Movement Stories ──────────────────────────────────────────────────────────
// Short-form testimony feed from around the world. Reaction/comment shape mirrors
// postsApi so the shared ReactionsSheet/comment UI can target it via a `movement`
// scope. Admin curation/moderation goes through /api/admin/movement-stories.
export const movementStoriesApi = {
  list:          (params = '')        => api.get(`/api/movement-stories${params}`),
  discover:      ()                   => api.get('/api/movement-stories/discover'),
  // Every mappable story, grouped into places, in one call — the globe, the pin
  // popups and the viewport list all read from this, so panning never refetches.
  map:           ()                   => api.get('/api/movement-stories/map'),
  // Hydrate full cards (incl. cover images, which /map deliberately omits) for a
  // bounded set of ids — used by the map's viewport list.
  byIds:         (ids)                => api.get(`/api/movement-stories?ids=${ids.slice(0, 60).join(',')}`),
  get:           (id)                 => api.get(`/api/movement-stories/${id}`),
  react:         (id, emoji)          => api.post(`/api/movement-stories/${id}/react`, { emoji }),
  comments:      (id)                 => api.get(`/api/movement-stories/${id}/comments`),
  comment:       (id, content, parentId) => api.post(`/api/movement-stories/${id}/comments`, { content, parentId }),
  deleteComment: (id, commentId)      => api.delete(`/api/movement-stories/${id}/comments/${commentId}`),
  submit:        (data)               => api.post('/api/movement-stories/submit', data),
  reactors:      (id, { emoji, cursor, limit } = {}) => {
    const qs = new URLSearchParams();
    if (emoji)  qs.set('emoji',  emoji);
    if (cursor) qs.set('cursor', cursor);
    if (limit)  qs.set('limit',  String(limit));
    const q = qs.toString();
    return api.get(`/api/movement-stories/${id}/reactions${q ? `?${q}` : ''}`);
  },
};

// ── Groups ───────────────────────────────────────────────────────────────────
export const groupsApi = {
  list:   ()   => api.get('/api/groups'),
  get:    (id) => api.get(`/api/groups/${id}`),
  create: (d)  => api.post('/api/groups', d),
  join:   (id) => api.post(`/api/groups/${id}/join`),
  leave:  (id) => api.post(`/api/groups/${id}/leave`),
};

// ── Prayer ───────────────────────────────────────────────────────────────────
export const prayerApi = {
  list:         ()      => api.get('/api/prayer'),
  calendar:     ()      => api.get('/api/prayer/calendar'),
  create:       (d)     => api.post('/api/prayer', d),
  update:       (id, d) => api.put(`/api/prayer/${id}`, d),
  pray:         (id)    => api.post(`/api/prayer/${id}/pray`),
  delete:       (id)    => api.delete(`/api/prayer/${id}`),
  coverPhotos:  ()      => api.get('/api/prayer/cover-photos'),
  setStage:     (id, stage) => api.post(`/api/prayer/${id}/witness-stage`, { stage }),
  logStep:      (id, type)  => api.post(`/api/prayer/${id}/witness-step`,  { type }),
};

// ── Outreach: story builder + invite links ───────────────────────────────────
// A user may keep several stories. CRUD + template-aware AI draft + per-story
// sharing all live under /api/outreach/stories.
export const outreachApi = {
  listStories:   ()      => api.get('/api/outreach/stories'),
  getStory:      (id)    => api.get(`/api/outreach/stories/${id}`),
  createStory:   (d)     => api.post('/api/outreach/stories', d),
  updateStory:   (id, d) => api.put(`/api/outreach/stories/${id}`, d),
  deleteStory:   (id)    => api.delete(`/api/outreach/stories/${id}`),
  generateStory: (id, d) => api.post(`/api/outreach/stories/${id}/generate`, d),
  generateCoverImage: (id, { force } = {}) => api.post(`/api/outreach/stories/${id}/cover${force ? '?force=true' : ''}`),
  shareStory:    (id)    => api.post(`/api/outreach/stories/${id}/share`),
};

// ── Weekly Evangelism Check-in ────────────────────────────────────────────────
// Sunday-evening rhythm: "Who did you share Jesus with this week?" — one
// check-in per week, with per-person sharing logs, streak + patterns.
export const evangelismApi = {
  current:       ()       => api.get('/api/evangelism/current'),
  list:          (weeks = 12) => api.get(`/api/evangelism/checkins?weeks=${weeks}`),
  save:          (d)      => api.post('/api/evangelism/checkins', d),
  shareToPrayer: (shareId) => api.post(`/api/evangelism/shares/${shareId}/to-prayer`),
};

export const invitesApi = {
  create: (d)      => api.post('/api/invites', d),
  claim:  (token)  => api.post(`/api/invites/${token}/claim`),
};

// ── Admin: Prayer Cover Photos ────────────────────────────────────────────────
export const prayerCoverAdminApi = {
  list:   (pool)         => api.get(`/api/admin/prayer-cover-photos?pool=${encodeURIComponent(pool)}`),
  create: (imageUrl, pool) => api.post('/api/admin/prayer-cover-photos', { imageUrl, pool }),
  delete: (id)           => api.delete(`/api/admin/prayer-cover-photos/${id}`),
};

// ── Courses ──────────────────────────────────────────────────────────────────
export const coursesApi = {
  list:        ()         => api.get('/api/courses'),
  get:         (id)       => api.get(`/api/courses/${id}`),
  getLesson:   (id, lid)  => api.get(`/api/courses/${id}/lessons/${lid}`),
  complete:    (id, lid)  => api.post(`/api/courses/${id}/lessons/${lid}/complete`),
};

// ── Profile ──────────────────────────────────────────────────────────────────
// Call once after login to issue CloudFront media cookies
export async function issueMediaCookies() {
  try { await api.post('/api/profile/media-cookies'); } catch {}
}

export const profileApi = {
  get:    ()   => api.get('/api/profile'),
  update: (d)  => api.put('/api/profile', d),
  export: ()   => api.get('/api/profile/export'),
};

// ── Users ─────────────────────────────────────────────────────────────────────
export const usersApi = {
  search:  (q = '', { interests } = {}) => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (interests) params.set('interests', interests);
    return api.get(`/api/users?${params.toString()}`);
  },
  getById: (id)      => api.get(`/api/users/${id}`),
};

// ── Connections ───────────────────────────────────────────────────────────────
export const connectionsApi = {
  list:        ()          => api.get('/api/connections'),
  suggestions: (limit = 10)=> api.get(`/api/connections/suggestions?limit=${limit}`),
  request:     (toUserId)  => api.post('/api/connections/request', { toUserId }),
  respond:     (id, action)=> api.put(`/api/connections/${id}`, { action }),
  remove:      (id)        => api.delete(`/api/connections/${id}`),
};

export const blocksApi = {
  list:    ()       => api.get('/api/blocks'),
  block:   (userId) => api.post(`/api/blocks/${userId}`),
  unblock: (userId) => api.delete(`/api/blocks/${userId}`),
};

// ── Auth (public — no JWT required) ──────────────────────────────────────────
export const authApi = {
  validateResetToken: (token)               => api.post('/api/auth/validate-reset-token', { token }),
  resetWithToken:     (token, newPassword)  => api.post('/api/auth/reset-with-token',     { token, newPassword }),
};

// ── Admin ─────────────────────────────────────────────────────────────────────
export const adminApi = {
  stats:            ()               => api.get('/api/admin/stats'),
  metrics:          ()               => api.get('/api/admin/metrics'),
  userActivity:        (params = '')    => api.get(`/api/admin/user-activity/summary${params}`),
  userActivityUsers:   (params = '')    => api.get(`/api/admin/user-activity/users${params}`),
  users:            (params = '')    => api.get(`/api/admin/users${params}`),
  setRole:             (id, role)       => api.put(`/api/admin/users/${id}/role`, { role }),
  resetPrayerEmail:    (id)             => api.post(`/api/admin/users/${id}/reset-prayer-email`),
  prayerEvents:        (params = '')    => api.get(`/api/admin/prayer/events${params}`),
  deletePrayerEvent:   (id, type = '')  => api.delete(`/api/admin/prayer/events/${id}${type ? `?type=${type}` : ''}`),
  runScheduler:        (priority)       => api.post(`/api/admin/prayer/run-scheduler/${priority}`),
  runWallScheduler:    ()               => api.post('/api/admin/prayer/run-wall-scheduler'),
  prayerRecipients:    (params = '')    => api.get(`/api/admin/prayer/recipients${params}`),
  logs:             (params = '')    => api.get(`/api/admin/logs${params}`),
  // Persisted browser/webview crashes. Separate from `logs`, which is an
  // in-memory per-task buffer that a redeploy wipes.
  clientErrors:       (params = '')  => api.get(`/api/admin/client-errors${params}`),
  clearClientErrors:  (id)           => api.delete(`/api/admin/client-errors${id ? `?id=${id}` : ''}`),
  posts:            (params = '')    => api.get(`/api/admin/posts${params}`),
  deletePost:       (id)             => api.delete(`/api/admin/posts/${id}`),
  // System prayer items
  systemPrayers:       ()            => api.get('/api/admin/system-prayers'),
  createSystemPrayer:  (d)           => api.post('/api/admin/system-prayers', d),
  updateSystemPrayer:  (id, d)       => api.put(`/api/admin/system-prayers/${id}`, d),
  deleteSystemPrayer:  (id)          => api.delete(`/api/admin/system-prayers/${id}`),
  // Audit logs
  auditLogs:           (params = '') => api.get(`/api/admin/audit-logs${params}`),
  auditLogActions:     ()            => api.get('/api/admin/audit-logs/actions'),
  // Growth plan admin
  publishGrowthPlan:   (id, published) => api.put(`/api/admin/growth/${id}/publish`, { published }),
  // User creation + masquerade
  createUser:          (data)          => api.post('/api/admin/users', data),
  masqueradeAs:        (id)            => api.post(`/api/admin/users/${id}/masquerade`),
  resendPasswordReset: (id)            => api.post(`/api/admin/users/${id}/resend-password-reset`),
  // Email campaigns
  emailCampaigns:          ()            => api.get('/api/admin/email-campaigns'),
  createEmailCampaign:     (data)        => api.post('/api/admin/email-campaigns', data),
  updateEmailCampaign:     (id, data)    => api.put(`/api/admin/email-campaigns/${id}`, data),
  deleteEmailCampaign:     (id)          => api.delete(`/api/admin/email-campaigns/${id}`),
  testEmailCampaign:       (data)        => api.post('/api/admin/email-campaigns/test', data),
  previewEmailCampaignSend:(id)          => api.post(`/api/admin/email-campaigns/${id}/send`, { confirm: false }),
  emailCampaignRecipients: (id)          => api.get(`/api/admin/email-campaigns/${id}/recipients`),
  sendEmailCampaign:       (id)          => api.post(`/api/admin/email-campaigns/${id}/send`, { confirm: true }),
  retryFailedCampaign:     (id)          => api.post(`/api/admin/email-campaigns/${id}/retry-failed`, {}),
  // Email send logs
  emailLogs:               (params = '') => api.get(`/api/admin/email-logs${params}`),
  // Communities — cross-community admin management
  communities: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') q.set(k, v); });
    const qs = q.toString();
    return api.get('/api/admin/communities' + (qs ? `?${qs}` : ''));
  },
  getCommunity:        (id)        => api.get(`/api/admin/communities/${id}`),
  updateCommunity:     (id, d)     => api.put(`/api/admin/communities/${id}`, d),
  deleteCommunity:     (id)        => api.delete(`/api/admin/communities/${id}`),
  freezeCommunity:     (id, frozen, reason) => api.post(`/api/admin/communities/${id}/freeze`, { frozen, reason }),
  transferCommunity:   (id, userId) => api.post(`/api/admin/communities/${id}/transfer`, { userId }),
  communityMembers:    (id)        => api.get(`/api/admin/communities/${id}/members`),
  // The Well — cross-community management
  wellThreads:             (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') q.set(k, v); });
    const qs = q.toString();
    return api.get('/api/admin/well/threads' + (qs ? `?${qs}` : ''));
  },
  getWellThread:           (id)          => api.get(`/api/admin/well/threads/${id}`),
  updateWellThread:        (id, data)    => api.put(`/api/admin/well/threads/${id}`, data),
  publishWellThread:       (id)          => api.post(`/api/admin/well/threads/${id}/publish`),
  archiveWellThread:       (id)          => api.post(`/api/admin/well/threads/${id}/archive`),
  deleteWellThread:        (id)          => api.delete(`/api/admin/well/threads/${id}`),
};

// ── Notifications ─────────────────────────────────────────────────────────────
export const notificationsApi = {
  list:        ()    => api.get('/api/notifications'),
  markRead:    (id)  => api.put(`/api/notifications/${id}/read`),
  markAllRead: ()    => api.put('/api/notifications/read-all'),
  delete:      (id)  => api.delete(`/api/notifications/${id}`),
  clearAll:    ()    => api.delete('/api/notifications'),
};

export const communitiesApi = {
  list:          (params) => api.get('/api/communities' + (params ? '?' + new URLSearchParams(params).toString() : '')),
  get:           (id)     => api.get(`/api/communities/${id}`),
  create:        (d)      => api.post('/api/communities', d),
  update:        (id, d)  => api.put(`/api/communities/${id}`, d),
  // Organizer feature switches. Takes a PARTIAL object — send only what
  // changed. Returns the full resolved `features`, with `zoom` already folded
  // against `calendar`, so the caller never has to reapply that rule itself.
  updateFeatures:(id, d)  => api.put(`/api/communities/${id}/features`, d),
  delete:        (id)     => api.delete(`/api/communities/${id}`),
  join:          (id)     => api.post(`/api/communities/${id}/join`),
  leave:         (id)     => api.post(`/api/communities/${id}/leave`),
  markViewed:    (id)     => api.post(`/api/communities/${id}/view`),
  getJoinRequests:   (id)            => api.get(`/api/communities/${id}/join-requests`),
  handleJoinRequest: (id, uid, action) => api.put(`/api/communities/${id}/join-requests/${uid}`, { action }),
  setMemberRole: (id, uid, role)     => api.put(`/api/communities/${id}/members/${uid}/role`, { role }),
  removeMember:  (id, uid)           => api.delete(`/api/communities/${id}/members/${uid}`),
  getPosts:      (id, p)             => api.get(`/api/communities/${id}/posts` + (p ? '?' + new URLSearchParams(p) : '')),
  getMessages:   (id, p)             => api.get(`/api/communities/${id}/messages` + (p ? '?' + new URLSearchParams(p) : '')),
  getInvites:    (id)                => api.get(`/api/communities/${id}/invites`),
  invite:        (id, userId)        => api.post(`/api/communities/${id}/invites`, { userId }),
  respondInvite: (id, inviteId, action) => api.put(`/api/communities/${id}/invites/${inviteId}`, { action }),
  getEvents:     (id)                => api.get(`/api/communities/${id}/events`),
  createEvent:   (id, d)             => api.post(`/api/communities/${id}/events`, d),
  updateEvent:   (id, eid, d)        => api.put(`/api/communities/${id}/events/${eid}`, d),
  deleteEvent:   (id, eid)           => api.delete(`/api/communities/${id}/events/${eid}`),
  getPlans:        (id)                => api.get(`/api/communities/${id}/reading-plans`),
  createPlan:      (id, d)             => api.post(`/api/communities/${id}/reading-plans`, d),
  updatePlan:      (id, pid, d)        => api.put(`/api/communities/${id}/reading-plans/${pid}`, d),
  deletePlan:      (id, pid)           => api.delete(`/api/communities/${id}/reading-plans/${pid}`),
  aiGeneratePlan:  (id, d)             => api.post(`/api/communities/${id}/ai-generate-plan`, d),
  getPlan:         (id, pid)           => api.get(`/api/communities/${id}/reading-plans/${pid}`),
  addPlanDay:      (id, pid, d)        => api.post(`/api/communities/${id}/reading-plans/${pid}/days`, d),
  saveResponse:    (id, pid, dayId, d) => api.put(`/api/communities/${id}/reading-plans/${pid}/days/${dayId}/response`, d),
  updateDay:       (id, pid, dayId, d) => api.put(`/api/communities/${id}/reading-plans/${pid}/days/${dayId}`, d),
  deleteDay:       (id, pid, dayId)    => api.delete(`/api/communities/${id}/reading-plans/${pid}/days/${dayId}`),
  generateDayAudio:(id, pid, dayId, opts) => api.post(`/api/communities/${id}/reading-plans/${pid}/days/${dayId}/audio`, opts || {}),
  getPlanParticipants: (id, pid)       => api.get(`/api/communities/${id}/reading-plans/${pid}/participants`),
  getPlanActivity:     (id, pid)       => api.get(`/api/communities/${id}/reading-plans/${pid}/activity`),
  exportPlan: async (id, pid, filename) => {
    const token = await getToken();
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${BASE}/api/communities/${id}/reading-plans/${pid}/export`, { headers });
    if (!res.ok) throw new Error(`Export failed: HTTP ${res.status}`);
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = filename || 'reading_plan.docx';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(url); document.body.removeChild(a); }, 1000);
  },
  refreshZoom:     (id)                => api.post(`/api/communities/${id}/zoom/refresh`),
  removeZoom:      (id)                => api.delete(`/api/communities/${id}/zoom`),
};

export const bibleApi = {
  passage: (ref, bibleId) => api.get(`/api/bible/passage?ref=${encodeURIComponent(ref)}&bibleId=${encodeURIComponent(bibleId)}`),
};

export const bibleStudyApi = {
  generate:   (d)  => api.post('/api/bible-study/generate', d),
  list:        ()  => api.get('/api/bible-study'),
  get:        (id) => api.get(`/api/bible-study/${id}`),
  saveAnswers: (id, d) => api.put(`/api/bible-study/${id}/answers`, d),
  delete:     (id) => api.delete(`/api/bible-study/${id}`),
  inspire:    (ctx) => api.post('/api/bible-study/inspire', ctx),
  // Sharing
  share:      (id, d) => api.post(`/api/bible-study/${id}/share`, d),
  unshare:    (id)    => api.post(`/api/bible-study/${id}/unshare`),
  getShared:  (token) => api.get(`/api/bible-study/shared/${token}`),
};

export const prayNowApi = {
  generate: (d)  => api.post('/api/pray-now', d),
  list:     ()   => api.get('/api/pray-now'),
  get:      (id) => api.get(`/api/pray-now/${id}`),
  delete:   (id) => api.delete(`/api/pray-now/${id}`),
};

// ── Push Notifications ────────────────────────────────────────────────────────
export const pushApi = {
  getVapidKey:     ()      => api.get('/api/push/vapid-public-key'),
  subscribe:       (data)  => api.post('/api/push/subscribe', data),
  unsubscribe:     (data)  => api.post('/api/push/unsubscribe', data),
  status:          ()      => api.get('/api/push/status'),
  registerDevice:  (data)  => api.post('/api/push/register-device', data),
};

// ── Prayer Wall ───────────────────────────────────────────────────────────────
export const prayerWallApi = {
  list:       ()           => api.get('/api/prayer-wall'),
  create:     (d)          => api.post('/api/prayer-wall', d),
  update:     (id, d)      => api.put(`/api/prayer-wall/${id}`, d),
  answer:     (id, note)   => api.post(`/api/prayer-wall/${id}/answer`, { answeredNote: note }),
  pray:       (id)         => api.post(`/api/prayer-wall/${id}/pray`),
  delete:     (id)         => api.delete(`/api/prayer-wall/${id}`),
  discover:         ()           => api.get('/api/prayer-wall/discover'),
  discoverSkipped:  ()           => api.get('/api/prayer-wall/discover/skipped'),
  swipe:            (id, action) => api.post(`/api/prayer-wall/${id}/swipe`, { action }),
  prayingFor:       ()           => api.get('/api/prayer-wall/praying-for'),
  addToCalendar:    (id)         => api.post(`/api/prayer-wall/${id}/add-to-calendar`),
  addToCalendarDate:(id, date)   => api.post(`/api/prayer-wall/${id}/add-to-calendar-date`, { date }),
  addUpdate:        (id, content) => api.post(`/api/prayer-wall/${id}/updates`, { content }),
  deleteUpdate:     (updateId)    => api.delete(`/api/prayer-wall/updates/${updateId}`),
  // Lazy translate fallback for legacy items created before multilingual was shipped.
  // Most items already include titleAlt/descriptionAlt because translation runs at create time.
  translateLegacy:        (id)        => api.post(`/api/prayer-wall/${id}/translate`),
  translateLegacyUpdate:  (updateId)  => api.post(`/api/prayer-wall/updates/${updateId}/translate`),
};

// ── Journal ───────────────────────────────────────────────────────────────────
export const journalApi = {
  timeline: (params = {}) => {
    const q = new URLSearchParams();
    if (params.from)  q.set('from',  params.from);
    if (params.to)    q.set('to',    params.to);
    if (params.types) q.set('types', params.types.join(','));
    return api.get(`/api/journal/timeline?${q.toString()}`);
  },
  activity: (params = {}) => {
    const q = new URLSearchParams();
    if (params.from) q.set('from', params.from);
    if (params.to)   q.set('to',   params.to);
    // Send browser TZ so day-key bucketing matches how the timeline groups
    // items on this device. Without this, items near midnight land on
    // different days in the dot strip vs the entries below.
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) q.set('tz', tz);
    } catch {}
    return api.get(`/api/journal/activity?${q.toString()}`);
  },
  listEntries:   ()           => api.get('/api/journal/entries'),
  createEntry:   (d)          => api.post('/api/journal/entries', d),
  updateEntry:   (id, d)      => api.put(`/api/journal/entries/${id}`, d),
  deleteEntry:   (id)         => api.delete(`/api/journal/entries/${id}`),
};

// ── The Well ─────────────────────────────────────────────────────────────────
export const wellApi = {
  // Settings
  getSettings:    (cid)        => api.get(`/api/well/settings/${cid}`),
  updateSettings: (cid, d)     => api.put(`/api/well/settings/${cid}`, d),

  // Threads (admin)
  listThreads:    (cid, q='')  => api.get(`/api/well/threads/${cid}${q ? `?${q}` : ''}`),
  getAdminThread: (cid, tid)   => api.get(`/api/well/threads/${cid}/${tid}`),
  createThread:   (cid, d)     => api.post(`/api/well/threads/${cid}`, d),
  updateThread:   (cid, tid, d)=> api.put(`/api/well/threads/${cid}/${tid}`, d),
  deleteThread:   (cid, tid)   => api.delete(`/api/well/threads/${cid}/${tid}`),
  publishThread:  (cid, tid)   => api.post(`/api/well/threads/${cid}/${tid}/publish`),
  scrapePreview:  (cid, d)     => api.post(`/api/well/threads/${cid}/scrape-preview`, d),
  fetchNow:       (cid, d)     => api.post(`/api/well/threads/${cid}/fetch-now`, d || {}),
  setVideo:       (cid, tid, videoUrl) => api.patch(`/api/well/threads/${cid}/${tid}/video`, { videoUrl }),

  // Public reads
  feed:           (cid, q='')  => api.get(`/api/well/feed/${cid}${q ? `?${q}` : ''}`),
  getThread:      (tid)        => api.get(`/api/well/thread/${tid}`),

  // Posts / replies
  listPosts:      (tid, q='')  => api.get(`/api/well/thread/${tid}/posts${q ? `?${q}` : ''}`),
  listReplies:    (postId)     => api.get(`/api/well/post/${postId}/replies`),
  createPost:     (tid, content) => api.post(`/api/well/thread/${tid}/posts`, { content }),
  reply:          (postId, content) => api.post(`/api/well/post/${postId}/reply`, { content }),
  editPost:       (postId, content) => api.put(`/api/well/post/${postId}`, { content }),
  deletePost:     (postId)     => api.delete(`/api/well/post/${postId}`),
  pinPost:        (postId)     => api.post(`/api/well/post/${postId}/pin`),
  reportPost:     (postId, reason) => api.post(`/api/well/post/${postId}/report`, { reason }),

  // Reactions
  react:          (d)          => api.post('/api/well/react', d),
  threadReactions:(tid)        => api.get(`/api/well/thread/${tid}/reactions`),
  reactors:       (scope, id, { type, cursor, limit } = {}) => {
    const qs = new URLSearchParams();
    if (type)   qs.set('type',   type);
    if (cursor) qs.set('cursor', cursor);
    if (limit)  qs.set('limit',  String(limit));
    const q = qs.toString();
    return api.get(`/api/well/${scope}/${id}/reactors${q ? `?${q}` : ''}`);
  },

  // Challenge
  submitChallenge: (tid, response) => api.post(`/api/well/thread/${tid}/challenge`, { response }),
  listChallenge:   (tid, q='')     => api.get(`/api/well/thread/${tid}/challenge${q ? `?${q}` : ''}`),

  // Private per-user sermon notes — many timestamped snippets per thread
  listMyNoteEntries:   (tid)                      => api.get(`/api/well/thread/${tid}/my-note-entries`),
  createMyNoteEntry:   (tid, content, color)      => api.post(`/api/well/thread/${tid}/my-note-entries`, { content, color }),
  updateMyNoteEntry:   (tid, eid, content, color) => api.put(`/api/well/thread/${tid}/my-note-entries/${eid}`, { content, color }),
  deleteMyNoteEntry:   (tid, eid)                 => api.delete(`/api/well/thread/${tid}/my-note-entries/${eid}`),
};

// ── Public API (no auth required) ────────────────────────────────────────────
async function publicJson(path, init = {}) {
  const res = await fetch(`${BASE}${path}`, init);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || err.message || `HTTP ${res.status}`);
  }
  return res.json();
}

export const publicApi = {
  getGrowthPlanPreview: (id) =>
    fetch(`${BASE}/api/growth/${id}/public`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); }),
  getPublicTestimony: (token) =>
    fetch(`${BASE}/api/outreach/public/testimony/${token}`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); }),
  getPublicBibleStudy: (token) =>
    fetch(`${BASE}/api/bible-study/public/${token}`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); }),
  getPublicMovementStory: (id, lang) =>
    fetch(`${BASE}/api/movement-stories/public/${id}${lang ? `?lang=${lang}` : ''}`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); }),
  getPublicPassage: (ref, bibleId) =>
    fetch(`${BASE}/api/bible/public/passage?ref=${encodeURIComponent(ref)}&bibleId=${encodeURIComponent(bibleId)}`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); }),
};

// ── Blog ─────────────────────────────────────────────────────────────────────
// Public read endpoints (no auth) plus admin CRUD + AI assist.
export const blogApi = {
  // Public
  list:      (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return publicJson(`/api/blog${q ? `?${q}` : ''}`);
  },
  get:       (slug)        => publicJson(`/api/blog/${encodeURIComponent(slug)}`),
  listTags:  ()            => publicJson('/api/blog/tags'),

  // Admin
  adminList:    (params = '')        => api.get(`/api/admin/blog${params}`),
  adminGet:     (id)                 => api.get(`/api/admin/blog/${id}`),
  adminPreview: (slug)               => api.get(`/api/admin/blog/preview/${encodeURIComponent(slug)}`),
  adminCreate:  (data)               => api.post('/api/admin/blog', data),
  adminUpdate:  (id, data)           => api.put(`/api/admin/blog/${id}`, data),
  adminDelete:  (id)                 => api.delete(`/api/admin/blog/${id}`),
  publish:      (id)                 => api.post(`/api/admin/blog/${id}/publish`),
  unpublish:    (id)                 => api.post(`/api/admin/blog/${id}/unpublish`),
  aiOutline:    (id, body = {})      => api.post(`/api/admin/blog/${id}/ai/outline`, body),
  aiBody:       (id, body = {})      => api.post(`/api/admin/blog/${id}/ai/body`, body),
};

// ── Newsletter ───────────────────────────────────────────────────────────────
// Public subscribe/unsubscribe + authenticated /me toggle for Settings.
export const newsletterApi = {
  // Public (no auth)
  subscribe:        (email, source = 'public_blog', honeypot) =>
    publicJson('/api/newsletter/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, source, honeypot }),
    }),
  unsubscribeGet:   (token)          => publicJson(`/api/newsletter/unsubscribe/${encodeURIComponent(token)}`),
  unsubscribePost:  (token, action)  => publicJson(`/api/newsletter/unsubscribe/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action }),
  }),

  // Authenticated user toggle
  getMe:            ()               => api.get('/api/newsletter/me'),
  setMe:            (subscribed)     => api.post('/api/newsletter/me', { subscribed }),

  // Admin: settings
  getSettings:      ()               => api.get('/api/admin/newsletter/settings'),
  updateSettings:   (data)           => api.put('/api/admin/newsletter/settings', data),
  createAudience:   ()               => api.post('/api/admin/newsletter/settings/create-audience'),
  testConnection:   ()               => api.post('/api/admin/newsletter/settings/test'),
  backfillUsers:    ()               => api.post('/api/admin/newsletter/settings/backfill-users'),

  // Admin: subscribers
  listSubscribers:  (params = '')    => api.get(`/api/admin/newsletter/subscribers${params}`),
  unsubscribe:      (id)             => api.delete(`/api/admin/newsletter/subscribers/${id}`),
  importSubscribers:(emails)         => api.post('/api/admin/newsletter/subscribers/import', { emails }),

  // Admin: issues
  listIssues:       ()               => api.get('/api/admin/newsletter/issues'),
  getIssue:         (id)             => api.get(`/api/admin/newsletter/issues/${id}`),
  createIssue:      (data)           => api.post('/api/admin/newsletter/issues', data),
  updateIssue:      (id, data)       => api.put(`/api/admin/newsletter/issues/${id}`, data),
  deleteIssue:      (id)             => api.delete(`/api/admin/newsletter/issues/${id}`),
  testSendIssue:    (id, toEmail)    => api.post(`/api/admin/newsletter/issues/${id}/test`, { toEmail }),
  sendIssue:        (id, scheduledAt = null) => api.post(`/api/admin/newsletter/issues/${id}/send`, { scheduledAt }),
  refreshStats:     (id)             => api.get(`/api/admin/newsletter/issues/${id}/stats`),
  aiBody:           (id, body = {})  => api.post(`/api/admin/newsletter/issues/${id}/ai/body`, body),

  // Admin: recurring templates
  listRecurring:    ()               => api.get('/api/admin/newsletter/recurring'),
  createRecurring:  (data)           => api.post('/api/admin/newsletter/recurring', data),
  updateRecurring:  (id, data)       => api.put(`/api/admin/newsletter/recurring/${id}`, data),
  deleteRecurring:  (id)             => api.delete(`/api/admin/newsletter/recurring/${id}`),
};

// ── Discipleship / spiritual lineage ─────────────────────────────────────────
export const discipleshipApi = {
  myTree:          ()                       => api.get('/api/discipleship/my-tree'),
  myDisciplers:    ()                       => api.get('/api/discipleship/my-disciplers'),
  myDisciples:     ()                       => api.get('/api/discipleship/my-disciples'),
  pending:         ()                       => api.get('/api/discipleship/pending'),
  mySentRequests:  ()                       => api.get('/api/discipleship/my-sent-requests'),
  request:         (targetUserId, role)     => api.post('/api/discipleship/request', { target_user_id: targetUserId, role }),
  accept:          (id)                     => api.post(`/api/discipleship/${id}/accept`),
  decline:         (id)                     => api.post(`/api/discipleship/${id}/decline`),
  end:             (id, endReason)          => api.post(`/api/discipleship/${id}/end`, endReason ? { end_reason: endReason } : {}),
  setPrimary:      (id)                     => api.post(`/api/discipleship/${id}/set-primary`),
};

// ── Coaching check-ins ─────────────────────────────────────────────────────────
// Coach-initiated check-ins layered on an active lineage relationship.
export const coachingApi = {
  templates:        ()                              => api.get('/api/coaching/templates'),
  resolve:          (otherUserId)                   => api.get(`/api/coaching/resolve?otherUserId=${encodeURIComponent(otherUserId)}`),
  thread:           (relId)                         => api.get(`/api/coaching/${relId}/checkins`),
  send:             (relId, templateKey, questions) => api.post(`/api/coaching/${relId}/checkins`, { templateKey, questions }),
  answer:           (relId, id, answers)            => api.put(`/api/coaching/${relId}/checkins/${id}/answers`, { answers }),
  respond:          (relId, id, body)               => api.put(`/api/coaching/${relId}/checkins/${id}/respond`, body),
  needsAnswer:      ()                              => api.get('/api/coaching/inbox/needs-answer'),
  needsResponse:    ()                              => api.get('/api/coaching/inbox/needs-response'),
  suggestQuestions: (relId)                         => api.post(`/api/coaching/${relId}/suggest-questions`),
};

// ── Analytics ────────────────────────────────────────────────────────────────
// Fire and forget: swallow errors so a logging failure never breaks the UI.
export const analyticsApi = {
  track: (event, properties = {}) =>
    api.post('/api/analytics/event', { event, properties }).catch(() => {}),
};
